import { Table, showDialogConfirm } from '@octano/global-ui';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../locales/translations';
import { useColumnsItems } from '../hooks/useColumnsItems';
import { ChildSearchItem, ChildType } from '../types';

interface ItemsAssignmentTableProps {
  courses: Map<number, ChildSearchItem>;
  groups: Map<number, ChildSearchItem>;
  onRemove: (id: number, type: ChildType) => void;
  onShowDetail: (id: number) => void;
}

/**
 * HU 15571 — Tabla local con las actividades + sub-agrupaciones acumuladas
 * en la sesión de edición. La acción "Eliminar" abre un confirm y, al
 * aceptar, llama `onRemove(id, type)` para actualizar los Maps en el
 * padre.
 *
 * Nota: La tabla NO se persiste hasta que el usuario presiona Guardar en
 * la vista padre (full-replace).
 */
export default function ItemsAssignmentTable({
  courses,
  groups,
  onRemove,
  onShowDetail,
}: ItemsAssignmentTableProps): ReactElement {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);

  const handleRemoveWithConfirm = (id: number, type: ChildType): void => {
    showDialogConfirm({
      icon: { name: 'warning', color: 'warning' },
      title: t('items.removeConfirm.title'),
      btnConfirm: {
        text: t('items.removeConfirm.confirm'),
        onConfirm: () => onRemove(id, type),
      },
      btnClose: { text: t('items.removeConfirm.cancel') },
    });
  };

  const rows = useMemo<ChildSearchItem[]>(
    () => [...Array.from(courses.values()), ...Array.from(groups.values())],
    [courses, groups],
  );

  const columns = useColumnsItems(handleRemoveWithConfirm, onShowDetail);

  return (
    <Table<ChildSearchItem>
      columns={columns}
      data={rows}
      noResultsText={
        <div className="text-center py-4 text-muted">
          {t('items.table.empty')}
        </div>
      }
    />
  );
}
